import { yupResolver } from "@hookform/resolvers/yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Divider, IconButton, InputAdornment } from "@mui/material";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getAuthActionState } from "app/reducers/authSlice";
import { confirmResetPassword, updatePassword } from "app/thunks/authAPI";
import classNames from "classnames";
import { NAVIGATOR } from "features/navigation/index";
import { UpdatePasswordData } from "interfaces/requests/Auth";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router";
import { PasswordIcon, Spinner } from "ui";
import { BUTTON_STYLES, BUTTON_TYPES } from "ui/Button";
import { TextField } from "ui/TextField";
import { useCustomToast } from "ui/Toast/hook";

import { validationUploadPasswordSchema } from "./validations";

export const UpdatePasswordScreen = () => {
  const { isLoading } = useAppSelector(getAuthActionState);
  const dispatch = useAppDispatch();
  const { showErrorToast } = useCustomToast();

  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const oobCode = queryParams.get("oobCode");

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    trigger,
    watch,
  } = useForm<UpdatePasswordData>({
    mode: "all",
    defaultValues: {
      newPassword: undefined,
      repeatPassword: undefined,
    },
    resolver: yupResolver(validationUploadPasswordSchema),
  });

  const [newPassword, repeatPassword] = watch([
    "newPassword",
    "repeatPassword",
  ]);

  const handleErrors = () => {
    history.push(NAVIGATOR.signIn);
    showErrorToast({
      title: "Error",
      message:
        "Ha ocurrido un error. Por favor ingrese las credenciales nuevamente.",
    });
  };

  const onSubmit = async (data: UpdatePasswordData) => {
    try {
      if (oobCode) {
        await dispatch(
          confirmResetPassword({
            newPassword: data.newPassword,
            code: oobCode,
          }),
        ).unwrap();
      } else {
        await dispatch(updatePassword(data)).unwrap();
      }
      history.push(NAVIGATOR.home);
    } catch (_) {
      handleErrors();
    }
  };

  return (
    <div className="flex flex-col max-w-xl px-8 pl-36">
      <div className="mb-10">
        <h1 className="text-white text-3xl mt-10 font-semibold font-orbitron">
          Actualiza tu contraseña
        </h1>
        <p className="text-white text-base mt-2 font-inter">
          Elige una contraseña que será utilizada para encriptar y asegurar tu
          cuenta.
        </p>
      </div>
      <Divider className="bg-gray-300" />
      <form className="flex flex-col mt-10 gap-2">
        <TextField
          {...register("newPassword")}
          name="newPassword"
          type={showNewPassword ? "text" : "password"}
          placeholder="Nueva contraseña"
          label="Nueva contraseña"
          errorMessage={errors.newPassword?.message}
          onChange={(event) => {
            register("newPassword").onChange(event);
            repeatPassword && trigger(["newPassword", "repeatPassword"]);
          }}
          startAdornment={<PasswordIcon />}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowNewPassword(!showNewPassword)}
                edge="end"
                sx={{ color: "#A4A4A4" }}
              >
                {showNewPassword ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          }
        />
        <TextField
          {...register("repeatPassword")}
          name="repeatPassword"
          type={showRepeatPassword ? "text" : "password"}
          placeholder="Contraseña"
          label="Contraseña"
          errorMessage={errors.repeatPassword?.message}
          autoComplete="new-password"
          onChange={(event) => {
            register("repeatPassword").onChange(event);
            newPassword && trigger(["newPassword", "repeatPassword"]);
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                edge="end"
                sx={{ color: "#A4A4A4" }}
              >
                {showRepeatPassword ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          }
        />
        <MUIButton
          variant="contained"
          disabled={!isValid || isLoading}
          onClick={handleSubmit(onSubmit)}
          className={classNames(
            BUTTON_STYLES[BUTTON_TYPES.SPACE_AUTH],
            "w-full",
          )}
        >
          {isLoading ? <Spinner color="white" /> : <p>Actualizar</p>}
        </MUIButton>
      </form>
    </div>
  );
};
